import React, { createContext, useContext, useState, useEffect } from "react";
import { getLoggedInUser } from "../helpers/authUtils";

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        user: getLoggedInUser(),
        token: null,
        loading: false,
        isUserLogout: false,
        error: null,
        passwordResetStatus: null,
    });

    const loginUser = () => {
        setAuthState((prev) => ({ ...prev, loading: true }));
    };

    const loginUserSuccess = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            user: payload.user,
            token: payload.token,
            loading: false,
            error: null,
        }));
    };

    const registerUser = () => {
        setAuthState((prev) => ({ ...prev, loading: true }));
    };

    const registerUserSuccess = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            user: payload.user,
            token: payload.token,
            loading: false,
            error: null,
        }));
    };

    const logoutUserSuccess = () => {
        setAuthState((prev) => ({
            ...prev,
            user: null,
            token: null,
            isUserLogout: true,
        }));
    };

    const forgetPassword = () => {
        setAuthState((prev) => ({ ...prev, loading: true }));
    };

    const forgetPasswordSuccess = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            passwordResetStatus: payload,
            loading: false,
            error: null,
        }));
    };

    const validateToken = () => {
        setAuthState((prev) => ({ ...prev, loading: true }));
    };

    const validateTokenSuccess = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            user: payload.user,
            token: payload.token,
            loading: false,
            error: null,
        }));
    };

    const validateTokenFailure = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            loading: false,
            error: payload,
            user: null,
            token: null,
            isUserLogout: true,
        }));
    };

    const refreshToken = () => {
        setAuthState((prev) => ({ ...prev, loading: true }));
    };

    const refreshTokenSuccess = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            token: payload,
            loading: false,
            error: null,
        }));
    };

    const refreshTokenFailure = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            loading: false,
            error: payload,
            user: null,
            token: null,
            isUserLogout: true,
        }));
    };

    const apiFailed = (payload) => {
        setAuthState((prev) => ({
            ...prev,
            loading: false,
            error: payload,
            isUserLogout: false,
        }));
    };

    const clearError = () => {
        setAuthState((prev) => ({
            ...prev,
            error: null,
        }));
    };

    const value = {
        ...authState,
        loginUser,
        loginUserSuccess,
        registerUser,
        registerUserSuccess,
        logoutUserSuccess,
        forgetPassword,
        forgetPasswordSuccess,
        validateToken,
        validateTokenSuccess,
        validateTokenFailure,
        refreshToken,
        refreshTokenSuccess,
        refreshTokenFailure,
        apiFailed,
        clearError,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
